import * as React from 'react'
import { Fragment, useCallback, useContext, useEffect, useMemo } from 'react'
import Typography from '@mui/material/Typography'
import Collapse from '@mui/material/Collapse'
import { AIIcon } from './Icons/AiIcon'
import { Chevron } from '@sales-center/components'
import Box from '@mui/material/Box'
import { PropertyDetailsDisplay } from './PropertyDetailsDisplay'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import {
  hasValidAddress,
  MeasureVariant,
  variantWidth,
} from './functions/functions'
import CloseIcon from '@mui/icons-material/Close'
import {
  LocationContext,
  LocationContextType,
} from '@user/components/EntityCards/Locations/LocationsPopoverButton/context/context'
import { useGetPropertyDetails } from './hooks/useGetPropertyDetails'

type Props = {
  address?: string
  entityType?: ENTITY_TYPES
  entityId?: number
  locations?: LocationDTO[]
  variant: MeasureVariant
  onClose?: () => void
  showBorder?: boolean
  iconColor?: string
}

export function Measure({
  address,
  entityType,
  entityId,
  variant,
  onClose,
  locations = [],
  showBorder = true,
  iconColor,
}: Props) {
  const [expanded, setExpanded] = React.useState<boolean>(false)

  const context = useContext<LocationContextType>(LocationContext)

  const locationsInfo = useMemo(
    () =>
      locations && locations.length
        ? locations
        : (context?.state?.locations ?? []),
    [locations, context?.state],
  )

  const {
    status,
    data: serviceLocations,
    getPropertyDetails,
  } = useGetPropertyDetails(address, entityType, entityId, locationsInfo)

  const openClosePropertyDetails = useCallback(() => {
    if (status === 'idle' && !serviceLocations && !expanded) {
      getPropertyDetails()
    }

    setExpanded(!expanded)
  }, [status, getPropertyDetails, expanded])

  useEffect(() => {
    if (
      status === 'idle' &&
      (expanded || variant === 'card' || variant === 'mapView')
    ) {
      getPropertyDetails()
    }
  }, [variant, status, expanded, getPropertyDetails])

  const isInValidRequest = useMemo(() => {
    if (locationsInfo && locationsInfo.length > 0) {
      return !hasValidAddress(locationsInfo)
    }
    return false
  }, [locationsInfo, status, serviceLocations])

  return isInValidRequest ? null : (
    <Fragment>
      {showBorder ? (
        <Box
          padding={1.2}
          pt={1}
          bgcolor="#f5e8ff"
          borderRadius={variant === 'card' ? '0px' : '12px'}
          width={variantWidth[variant]}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={0}
          >
            <Box display="flex" alignItems="center">
              <AIIcon />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 500, marginLeft: '8px' }}
                component="span"
              >
                Property Details
              </Typography>
            </Box>
            {variant === 'card' ? (
              <Box
                display="flex"
                alignItems="center"
                sx={{ cursor: 'pointer' }}
                onClick={onClose}
                data-test-id="property-details-close-button"
              >
                <CloseIcon fontSize="small" />
              </Box>
            ) : (
              <Box
                onClick={openClosePropertyDetails}
                sx={{ cursor: 'pointer' }}
                data-test-id="property-details-close-button"
              >
                <Chevron
                  open={expanded}
                  dataTestId="propertyDetailsExpandChevron"
                />
              </Box>
            )}
          </Box>
          <Collapse
            in={variant === 'card' ? true : expanded}
            timeout="auto"
            unmountOnExit
            sx={{ marginTop: '0px' }}
          >
            <Box pt={1.2}>
              <Box
                pt={1.4}
                display="flex"
                justifyContent="center"
                borderRadius="4px"
                overflow="hidden"
                sx={{ backgroundColor: '#fff' }}
              >
                <PropertyDetailsDisplay
                  locations={serviceLocations || []}
                  variant={variant}
                  status={status}
                  address={address}
                  entityId={entityId}
                  entityType={entityType}
                />
              </Box>
            </Box>
          </Collapse>
        </Box>
      ) : (
        <PropertyDetailsDisplay
          locations={serviceLocations || []}
          variant={variant}
          status={status}
          address={address}
          iconColor={iconColor}
        />
      )}
    </Fragment>
  )
}
